/* eslint-disable react-refresh/only-export-components */
import { useGetList } from "ra-core";
import { useMemo } from "react";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact, Deal, ScoringRule } from "../types";

/**
 * Native lead scoring. Every rule is a signal read from the contact's
 * engagement - email activity, repeat site visits, inbound source, recency,
 * status, seniority, open deals - worth the points configured in
 * Marketing → Workflows → Lead scoring. Scores cap at 100.
 *
 * The demo computes from generated touches and fields; production computes
 * the same rules over spine events (email.replied, touch.captured,
 * form.submitted, …), so scores update as behavior happens.
 */

const SENIOR = /\b(vp|vice president|director|chief|cto|ceo|cfo|cmo|coo|founder|head|president|owner|partner)\b/i;

export interface LeadScore {
  score: number;
  grade: "A" | "B" | "C" | "D";
  reasons: string[];
}

export const computeLeadScore = (
  contact: Contact,
  rules: ScoringRule[],
  openDealContactIds?: Set<Contact["id"]>,
): LeadScore => {
  const pts = (key: string) => {
    const r = rules.find((x) => x.key === key);
    return r && r.enabled !== false ? r.points : 0;
  };

  let score = 0;
  const reasons: string[] = [];
  const add = (key: string, reason: string, hit: boolean) => {
    const p = pts(key);
    if (hit && p > 0) {
      score += p;
      reasons.push(`${reason} (+${p})`);
    }
  };

  const touches = contact.touches ?? [];
  const daysSince = (iso?: string) =>
    iso ? (Date.now() - new Date(iso).getTime()) / 86400_000 : Infinity;

  add(
    "email_engagement",
    "Engages with email",
    touches.some((t) => t.channel === "email"),
  );
  add("site_return", "Repeat site visitor", touches.length >= 3);
  add(
    "inbound",
    "Inbound (form / search / campaign)",
    touches.some(
      (t) => t.channel === "organic" || t.channel === "paid" || !!t.landingPage,
    ),
  );
  add("recent_activity", "Active this week", daysSince(contact.last_seen) <= 7);
  add(
    "active_month",
    "Active this month",
    daysSince(contact.last_seen) > 7 && daysSince(contact.last_seen) <= 30,
  );
  add("status_hot", "Marked hot", contact.status === "hot");
  add("status_warm", "Marked warm", contact.status === "warm");
  add("seniority", "Senior title", SENIOR.test(contact.title ?? ""));
  add("newsletter", "Newsletter subscriber", !!contact.has_newsletter);
  add(
    "open_deal",
    "On an open deal",
    openDealContactIds?.has(contact.id) ?? false,
  );

  score = Math.min(100, score);
  const grade = score >= 65 ? "A" : score >= 45 ? "B" : score >= 25 ? "C" : "D";
  return { score, grade, reasons };
};

/** Contact ids that sit on at least one open deal. */
export const useOpenDealContactIds = (): Set<Contact["id"]> => {
  const { data: deals = [] } = useGetList<Deal>("deals", {
    pagination: { page: 1, perPage: 1000 },
  });
  return useMemo(() => {
    const ids = new Set<Contact["id"]>();
    for (const d of deals) {
      if (d.stage === "won" || d.stage === "lost" || d.archived_at) continue;
      for (const id of d.contact_ids ?? []) ids.add(id);
    }
    return ids;
  }, [deals]);
};

export const useLeadScore = (contact?: Contact): LeadScore | null => {
  const { scoringRules } = useConfigurationContext();
  const openIds = useOpenDealContactIds();
  return useMemo(() => {
    if (!contact) return null;
    // Live workspaces: the automation worker persists the real score
    // (rules over spine events + manual workflow adjustments) - show THAT
    // number, the one branches and triggers act on. The client-side
    // computation stays as the preview/demo fallback.
    if (typeof contact.score === "number" && contact.score > 0) {
      const s = contact.score;
      const grade = s >= 65 ? "A" : s >= 45 ? "B" : s >= 25 ? "C" : "D";
      return {
        score: s,
        grade,
        reasons: ["Computed live from this contact's activity by your scoring rules"],
      };
    }
    return computeLeadScore(contact, scoringRules ?? [], openIds);
  }, [contact, scoringRules, openIds]);
};

const GRADE_COLOR: Record<LeadScore["grade"], string> = {
  A: "#8fb586",
  B: "#2e6ba9",
  C: "#d9b36b",
  D: "#94a3b8",
};

/** Compact score badge - number + grade, tooltip lists the reasons. */
export const LeadScoreBadge = ({
  score,
  size = "md",
}: {
  score: LeadScore;
  size?: "sm" | "md";
}) => (
  <span
    title={
      score.reasons.length
        ? `Lead score ${score.score}/100\n${score.reasons.join("\n")}`
        : `Lead score ${score.score}/100`
    }
    className={`inline-flex items-center gap-1 rounded-full font-semibold tabular-nums text-white ${
      size === "sm" ? "px-1.5 py-0.5 text-[10px]" : "px-2 py-0.5 text-xs"
    }`}
    style={{ backgroundColor: GRADE_COLOR[score.grade] }}
  >
    {score.score}
    <span className="font-normal opacity-90">{score.grade}</span>
  </span>
);
